import { useState, useEffect, useMemo } from 'react';

import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import moment from 'moment';

import { compareRangeAtom, compareStartDateAtom, compareEndDateAtom } from '../../../atoms';
import { generateCompareRanges, getCompareDates } from '../helpers';

export const useComparison = (startDate, endDate, firstReportingDate) => {
  const [compare, setCompare] = useState(false);
  const [compareRange, setCompareRange] = useAtom(compareRangeAtom);
  const [compareStartDate, setCompareStartDate] = useAtom(compareStartDateAtom);
  const [compareEndDate, setCompareEndDate] = useAtom(compareEndDateAtom);
  const [compareStart, setCompareStart] = useState(undefined);
  const [compareEnd, setCompareEnd] = useState(undefined);
  const resetCompareRange = useResetAtom(compareRangeAtom);
  const resetCompareStartDate = useResetAtom(compareStartDateAtom);
  const resetCompareEndDate = useResetAtom(compareEndDateAtom);

  useEffect(() => {
    setCompare(compareRange !== '');
    if (compareRange === 'custom' && compareStartDate && compareEndDate) {
      setCompareStart(compareStartDate);
      setCompareEnd(compareEndDate);
    }
  }, []);

  useEffect(() => {
    if (compareRange === 'custom' && compareStartDate && compareEndDate) {
      setCompareStart(compareStartDate);
      setCompareEnd(compareEndDate);
    }
  }, [compareRange, compareStartDate, compareEndDate]);

  useEffect(() => {
    if (!compareRange) return setCompare(false);
    const isCustom = compareRange === 'custom';
    const [compareStartMoment, compareEndMoment] = getCompareDates(compareRange, startDate, endDate);

    const formatStartDate = moment(compareStartMoment).format('YYYY-MM-DD');
    const formatEndDate = moment(compareEndMoment).format('YYYY-MM-DD');

    setCompare(true);
    setCompareStart(!isCustom ? formatStartDate : compareStartDate);
    setCompareEnd(!isCustom ? formatEndDate : compareEndDate);
  }, [compareRange, startDate, endDate, compareStartDate, compareEndDate]);

  const onCompareDateChange = rangeKey => {
    setCompareRange(rangeKey);
    setCompare(rangeKey !== '');
    if (rangeKey !== 'custom') {
      resetCompareStartDate();
      resetCompareEndDate();
    }
    if (rangeKey === '') resetCompareRange();
  };

  const numDays = useMemo(() => {
    if (startDate === undefined || endDate === undefined) return;
    return moment(endDate).diff(moment(startDate), 'days') + 1;
  }, [startDate, endDate]);

  const isCustom = compareRange === 'custom';
  const compareOptions = generateCompareRanges(startDate, endDate, firstReportingDate, isCustom, compareStartDate, compareEndDate);

  return {
    compare, setCompare, compareStartDate, setCompareStartDate, compareEndDate, setCompareEndDate, compareStart, compareEnd, numDays, compareRange, setCompareRange,
    onCompareDateChange, compareOptions,
  };
};
